// Pivot view of the portfolio analysis data
// react
import { Suspense, useMemo } from 'react';
import { useSelector } from 'react-redux';

// internationalization
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, Link, useParams } from '@tanstack/react-router';

// material ui icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterListIcon from '@mui/icons-material/FilterList';
import PivotTableChartIcon from '@mui/icons-material/PivotTableChart';

// material ui
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGridPremium,
  ExportCsv,
  FilterPanelTrigger,
  GridColDef,
  GridSidebarValue,
  PivotPanelTrigger,
} from '@mui/x-data-grid-premium';

// api — generated
import {
  type AnalysisType,
  useGetPortfolioAnalysisPortfolioAnalysisAnalysisTypeAccountIdGetQuery,
} from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysis';
// api — generated
import {
  useGetLinkedEnergyGroupsLinkedEnergyGroupsAccountIdGetQuery,
  useGetPaMetersPortfolioMetersAnalysisTypeAccountIdGetQuery,
} from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisMeters';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import DataGridQuickFilter from '@/components/ui/datagrid/DataGridQuickFilter';
import PresetSelector from '@/components/ui/datagrid/PresetSelector';

// components
import { DegreeDayCorrectionToggle } from '@/features/energyxplorer/portfolio/components/datagrid/PortfolioToolbarButtons';
import { usePAColumns } from '@/features/energyxplorer/portfolio/hooks/pa-columns';
import usePortfolioDegreeDayCorrection from '@/features/energyxplorer/portfolio/hooks/usePortfolioDegreeDayCorrection';
import { getPresets } from '@/features/energyxplorer/portfolio/utils/pa-presets';
import { useDatagridLocaleText } from '@/hooks/useDatagridLocaleText';
import { useLocalStorageJson } from '@/hooks/useLocalStorageJson';
import useSearchParams from '@/hooks/useStableSearchParams';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';

export const Route = createFileRoute(
  '/$accountId/_user/energyxplorer/portfolio/$analysisType/pivot',
)({
  component: RouteComponent,
  staticData: {
    crumb: () => t('portfolio-analysis:pages.pivot_title'),
  },
});

function RouteComponent() {
  const localeText = useDatagridLocaleText();

  const account = useSelector(getSelectedAccount);
  const { analysisType } = useParams({ strict: false });
  const { t } = useTranslation('portfolio-analysis');
  const [searchParams] = useSearchParams();
  const { isDegreeDayCorrectionEnabled, setDegreeDayCorrection } =
    usePortfolioDegreeDayCorrection(analysisType as AnalysisType);

  const {
    data: rawData,
    error,
    isLoading,
    isFetching,
  } = useGetPortfolioAnalysisPortfolioAnalysisAnalysisTypeAccountIdGetQuery(
    {
      accountId: account?.AccountId,
      analysisType: analysisType as AnalysisType,
      degreeDayCorrection:
        analysisType === 'heat'
          ? isDegreeDayCorrectionEnabled || undefined
          : undefined,
    },
    { skip: !account?.AccountId },
  );

  // Fetch meter data
  const { data: powerMeterData } =
    useGetPaMetersPortfolioMetersAnalysisTypeAccountIdGetQuery(
      { analysisType: 'power', accountId: account?.AccountId },
      {
        skip: !account?.AccountId || analysisType !== 'power',
        pollingInterval: 1000 * 60 * 5,
        skipPollingIfUnfocused: true,
      } as Record<string, unknown>,
    );

  const { data: waterMeterData } =
    useGetPaMetersPortfolioMetersAnalysisTypeAccountIdGetQuery(
      { analysisType: 'water', accountId: account?.AccountId },
      {
        skip: !account?.AccountId || analysisType !== 'water',
        pollingInterval: 1000 * 60 * 5,
        skipPollingIfUnfocused: true,
      } as Record<string, unknown>,
    );

  const { data: heatMeterData } =
    useGetPaMetersPortfolioMetersAnalysisTypeAccountIdGetQuery(
      { analysisType: 'heat', accountId: account?.AccountId },
      {
        skip: !account?.AccountId || analysisType !== 'heat',
        pollingInterval: 1000 * 60 * 5,
        skipPollingIfUnfocused: true,
      } as Record<string, unknown>,
    );

  // Fetch linked energy groups
  const { data: linkedEnergyGroupsData } =
    useGetLinkedEnergyGroupsLinkedEnergyGroupsAccountIdGetQuery(
      { accountId: account?.AccountId },
      { skip: !account?.AccountId },
    );

  // Get the appropriate data based on analysis type
  const data = useMemo(() => {
    if (analysisType === 'power' || !rawData?.portfolio_analysis_list) {
      return rawData;
    }
    return {
      ...rawData,
      portfolio_analysis_list: rawData.portfolio_analysis_list.map((row) => ({
        ...row,
        metering_point_id: (row as Record<string, unknown>).data_stream
          ? (row as { data_stream: { unit_id?: unknown } }).data_stream.unit_id
          : undefined,
      })),
    };
  }, [rawData, analysisType]);

  const meterData =
    analysisType === 'power'
      ? powerMeterData
      : analysisType === 'heat'
        ? heatMeterData
        : waterMeterData;

  // Enrich data with meter information
  const enrichedData = useMemo(() => {
    if (data == null) return null;

    const portfolioList = data.portfolio_analysis_list.filter(
      (item) => item.matrikel_key,
    );
    const linkedEnergyGroups = linkedEnergyGroupsData?.LinkedEnergyGroups || [];

    return {
      ...data,
      portfolio_analysis_list: portfolioList.map((o) => {
        const meterEntry =
          meterData?.find(
            (b) => `${b.EjerlavKode}_${b.MatrikelNr}` == o.matrikel_key,
          ) || {};
        return {
          ...o,
          ...meterEntry,
          type: analysisType,
          linked_energy_groups: linkedEnergyGroups
            ?.find((leg) => {
              return leg.MatrikelKey === o.matrikel_key;
            })
            ?.LinkedEnergyGroups.filter((group) => {
              return !group.type?.toLowerCase().includes('portfolio');
            }),
        };
      }),
    };
  }, [data, meterData, analysisType, linkedEnergyGroupsData]);

  // Transform data for DataGrid (add IDs and handle nulls)
  const transformedData = useMemo(() => {
    if (!enrichedData) return null;

    return {
      ...enrichedData,
      portfolio_analysis_list: enrichedData.portfolio_analysis_list.map(
        (item, index) => ({
          ...item,
          id: index,
          average_weekday_night: item.average_weekday_night ?? null,
          inactive_active_ratio: item.inactive_active_ratio ?? null,
          meter_in_sync: item.meter_in_sync,
        }),
      ),
    };
  }, [enrichedData]);

  // Get presets for column grouping
  const presets = useMemo(() => {
    if (!transformedData) return [];
    return getPresets(transformedData, t, analysisType);
  }, [transformedData, t, analysisType]);

  // Always use the detailed preset (index 6) for row grouping
  const detailedPresetIndex = 6;

  // Get columns for pivot mode
  const columns = (
    usePAColumns(
      transformedData,
      analysisType,
      presets,
      detailedPresetIndex,
    ) as GridColDef[]
  ).filter(
    (col) =>
      !['type', 'favorites', 'warnings', 'ExtractedBuildingUse'].includes(
        col.field,
      ),
  ); // Remove 'address' from columns as it's used for row grouping

  // State for managing preset selection and pivot model
  const [dataGridState, setDataGridState] = useLocalStorageJson(
    `${analysisType}-Pivot-DataGrid-Presets`,
    {
      presets: [
        {
          name: 'Default',
          visibilityList: {},
          density: 'standard',
          sortModel: [],
          filterModel: { items: [], quickFilterValues: [] },
          pivotModel: {
            rows: [{ field: 'address' }],
            columns: [],
            values: [
              { field: 'average_all', aggFunc: 'avg' },
              { field: 'total_consumption_year', aggFunc: 'sum' },
            ],
          },
        },
      ],
      chosenPreset: 0,
    },
  );

  const updateChosenPreset = (patch) =>
    setDataGridState((prev) => ({
      ...prev,
      presets: prev.presets.map((preset, idx) =>
        idx === prev.chosenPreset ? { ...preset, ...patch } : preset,
      ),
    }));

  const currentPreset = dataGridState.presets[dataGridState.chosenPreset];
  // Handle pivot model changes and save to current preset
  const handlePivotModelChange = (newModel) => {
    updateChosenPreset({ pivotModel: newModel });
  };
  const currentPivotModel = currentPreset?.pivotModel || {
    rows: [],
    columns: [],
    values: [],
  };
  // Initial state for DataGrid - open pivot panel by default
  const initialState = {
    pivoting: {
      model: currentPivotModel,
      active: true,
    },
    sidebar: {
      open: true,
      value: GridSidebarValue.Pivot,
    },
  };

  // Errors from the portfolio data query are handled by apiErrorMiddleware.
  const CustomToolbar = () => (
    <Toolbar
      disableGutters
      sx={{ justifyContent: 'space-between', width: '100%' }}
    >
      <PresetSelector
        dataGridState={dataGridState}
        setDataGridState={setDataGridState}
      />

      <Box sx={{ display: 'flex', gap: 1 }}>
        {analysisType === 'heat' && (
          <DegreeDayCorrectionToggle
            checked={isDegreeDayCorrectionEnabled}
            onToggle={setDegreeDayCorrection}
          />
        )}
        <Tooltip title={t('toolbar.filters')}>
          <FilterPanelTrigger render={<Button />}>
            <FilterListIcon fontSize="small" />
          </FilterPanelTrigger>
        </Tooltip>
        <Tooltip title={t('toolbar.pivot')}>
          <PivotPanelTrigger
            render={(triggerProps, state) => (
              <Button
                {...triggerProps}
                color={state.active ? 'primary' : 'inherit'}
              />
            )}
          >
            <PivotTableChartIcon fontSize="small" />
          </PivotPanelTrigger>
        </Tooltip>
        <Tooltip title={t('toolbar.download_csv')}>
          <ExportCsv render={<Button />}>
            <FileDownloadIcon fontSize="small" />
          </ExportCsv>
        </Tooltip>
        <DataGridQuickFilter placeholder="Søg..." label="Søg" />
      </Box>
    </Toolbar>
  );
  return (
    <PageContainer maxWidth="xl">
      <Box mb={1} display="flex" alignItems="center" gap={1}>
        <Link
          to={
            '/$accountId/energyxplorer/portfolio/$analysisType/analysis/overview'
          }
          search={Object.fromEntries(searchParams)}
        >
          <IconButton
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'transparent',
                color: 'primary.main',
              },
              '&:hover .arrow-icon': {
                transform: 'translateX(-4px)',
                transition: 'transform 0.2s ease',
              },
            }}
          >
            <ArrowBackIcon className="arrow-icon" fontSize="small" />
          </IconButton>
        </Link>

        <Typography variant="h6" fontWeight={600}>
          {t('pages.pivot_title')}
        </Typography>
      </Box>
      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '80vh',
            verticalAlign: 'center',
          }}
        >
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 4 }}>
            {t('pages.loading_analysis')}
          </Typography>
        </Box>
      ) : (
        <>
          {error && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Card
                sx={{
                  p: 2,
                  mb: 2,
                  mt: 8,
                  borderRadius: 2,
                  width: { xs: '100%', sm: '80%', md: '60%', lg: '60%' },
                }}
              >
                <CardContent
                  sx={{
                    width: { xs: '100%', sm: '80%', md: '60%', lg: '60%' },
                  }}
                >
                  <Typography variant="h6">
                    {t('pages.no_analysis_found')}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {t('pages.no_analysis_found_description')}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {transformedData && !error && (
            <Suspense
              fallback={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    height: '80vh',
                    verticalAlign: 'center',
                  }}
                >
                  <CircularProgress />
                </Box>
              }
            >
              <Box
                sx={{
                  height: 'calc(100vh - 200px)',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <DataGridPremium
                  rows={transformedData.portfolio_analysis_list || []}
                  columns={columns}
                  initialState={initialState}
                  showToolbar
                  pivotModel={currentPivotModel}
                  loading={isFetching}
                  localeText={localeText}
                  columnHeaderHeight={56}
                  columnGroupHeaderHeight={48}
                  density="compact"
                  disableRowSelectionOnClick
                  onPivotModelChange={handlePivotModelChange}
                  pivotActive
                  slots={{
                    toolbar: CustomToolbar,
                  }}
                  sx={{
                    '& .MuiDataGrid-columnHeaders': {
                      minHeight: '56px !important',
                      maxHeight: '56px !important',
                    },
                    '& .MuiDataGrid-columnHeader': {
                      height: '56px !important',
                    },
                    '& .MuiDataGrid-columnHeaderRow': {
                      minHeight: '56px !important',
                      maxHeight: '56px !important',
                    },
                    '& .MuiDataGrid-columnHeader--filledGroup': {
                      minHeight: '48px !important',
                      maxHeight: '48px !important',
                      '& .MuiDataGrid-columnHeaderTitleContainer': {
                        overflow: 'visible',
                      },
                      '& .MuiDataGrid-columnHeaderTitleContainerContent': {
                        position: 'sticky',
                        left: 8,
                      },
                    },
                  }}
                />
              </Box>
            </Suspense>
          )}
        </>
      )}
    </PageContainer>
  );
}
